/**
 * 테이블 관련 기능을 담당하는 JavaScript 모듈
 */

// 테이블 관리자 객체
const tableManager = {
  /**
   * 테이블 열 너비를 동적으로 설정
   * @param {string} tableSelector - 테이블 선택자
   * @param {Object} options - 설정 옵션
   */
  initColumnWidths: function(tableSelector = '.theme-table', options = {}) {
    const tables = document.querySelectorAll(tableSelector);
    
    // 기본 옵션 설정
    const defaultOptions = {
      equalWidth: true,        // 모든 열을 균등하게 분배할지 여부
      firstColumnWidth: 6,     // 첫 번째 열 너비 (%)
      lastColumnWidth: 6,      // 마지막 열 너비 (%)
      fixedColumns: {}         // 고정 너비를 적용할 열 (인덱스: 너비)
    };
    
    // 옵션 병합
    const settings = {...defaultOptions, ...options};
    
    tables.forEach(table => {
      const headers = table.querySelectorAll('th');
      if (headers.length === 0) return;
      
      // 균등 분배 모드
      if (settings.equalWidth) {
        const equalColumnWidth = 100 / headers.length;
        
        // 모든 열에 동일한 너비 적용
        headers.forEach(header => {
          header.style.width = `${equalColumnWidth}%`;
        });
        
        // 디버깅 정보 출력
        console.log('테이블 열 너비 설정 (균등 분배):', {
          총열수: headers.length,
          열너비: equalColumnWidth
        });
        
        return;
      }
      
      // 고정 너비 모드 (equalWidth가 false인 경우)
      const fixedColumns = {
        0: settings.firstColumnWidth,                // 첫 번째 열(체크박스)
        [headers.length - 1]: settings.lastColumnWidth,  // 마지막 열
        ...settings.fixedColumns                     // 사용자 지정 고정 열
      };
      
      // 고정 열이 차지하는 총 너비 계산
      const fixedColumnsWidth = Object.values(fixedColumns).reduce((sum, width) => sum + width, 0);
      
      // 가변 열 개수 계산
      const flexibleColumnsCount = headers.length - Object.keys(fixedColumns).length;
      
      // 가변 열의 너비 계산 (남은 공간을 균등하게 분배)
      const flexibleColumnWidth = flexibleColumnsCount > 0 ? (100 - fixedColumnsWidth) / flexibleColumnsCount : 0;
      
      // 각 열에 너비 적용
      headers.forEach((header, index) => {
        if (index in fixedColumns) {
          header.style.width = `${fixedColumns[index]}%`;
        } else {
          header.style.width = `${flexibleColumnWidth}%`;
        }
      });
      
      // 디버깅 정보 출력
      console.log('테이블 열 너비 설정 (고정 너비):', {
        총열수: headers.length,
        고정열: Object.keys(fixedColumns).length,
        가변열: flexibleColumnsCount,
        고정열너비: fixedColumnsWidth,
        가변열너비: flexibleColumnWidth
      });
    });
  },
  
  /**
   * 테이블 체크박스 기능 초기화
   * @param {string} tableSelector - 테이블 선택자
   */
  initCheckboxes: function(tableSelector = '.theme-table') {
    const tables = document.querySelectorAll(tableSelector);
    
    tables.forEach(table => {
      const selectAllCheckbox = table.querySelector('thead input[type="checkbox"]');
      const rowCheckboxes = table.querySelectorAll('tbody input[type="checkbox"]');
      
      if (!selectAllCheckbox) return;
      
      // 전체 선택 체크박스 이벤트
      selectAllCheckbox.addEventListener('change', function() {
        rowCheckboxes.forEach(checkbox => {
          checkbox.checked = selectAllCheckbox.checked;
        });
      });
      
      // 개별 체크박스 이벤트
      rowCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', function() {
          const allChecked = Array.from(rowCheckboxes).every(cb => cb.checked);
          const someChecked = Array.from(rowCheckboxes).some(cb => cb.checked);
          
          selectAllCheckbox.checked = allChecked;
          selectAllCheckbox.indeterminate = someChecked && !allChecked;
        });
      });
    });
  },
  
  /**
   * 테이블 행 클릭 이벤트 초기화
   * @param {string} tableSelector - 테이블 선택자
   * @param {Function} callback - 클릭 시 실행할 콜백 함수
   */
  initRowClick: function(tableSelector = '.theme-table', callback) {
    const tables = document.querySelectorAll(tableSelector);
    
    tables.forEach(table => {
      const rows = table.querySelectorAll('tbody tr');
      
      rows.forEach(row => {
        row.addEventListener('click', function(e) {
          // 버튼 클릭은 무시 (버튼에 자체 이벤트가 있음)
          if (e.target.tagName === 'BUTTON' || e.target.closest('button') || 
              e.target.tagName === 'I' || e.target.closest('i')) {
            return;
          }
          
          // 체크박스 클릭은 무시
          if (e.target.type === 'checkbox' || e.target.closest('.checkbox-container')) {
            return;
          }
          
          // 콜백 함수 실행
          if (typeof callback === 'function') {
            callback(this, e);
          }
        });
      });
    });
  },
  
  /**
   * 테이블 필터링 기능 초기화
   * @param {string} filterSelector - 필터 선택자
   * @param {string} tableSelector - 테이블 선택자
   */
  initFilter: function(filterSelector = '.filter-dropdown select', tableSelector = '.theme-table') {
    const filterSelect = document.querySelector(filterSelector);
    const table = document.querySelector(tableSelector);
    
    if (!filterSelect || !table) return;
    
    filterSelect.addEventListener('change', function() {
      const sortBy = this.value;
      const rows = Array.from(table.querySelectorAll('tbody tr'));
      
      // 정렬 기준에 따라 행 정렬
      rows.sort((a, b) => {
        if (sortBy === '최신순') {
          // 날짜 또는 번호 기준 정렬 (내림차순)
          const dateA = a.querySelector('td:nth-child(5)').textContent;
          const dateB = b.querySelector('td:nth-child(5)').textContent;
          return dateB.localeCompare(dateA);
        } else if (sortBy === '이름순') {
          // 이름 기준 정렬 (오름차순)
          const nameA = a.querySelector('td:nth-child(3)').textContent;
          const nameB = b.querySelector('td:nth-child(3)').textContent;
          return nameA.localeCompare(nameB);
        } else if (sortBy === '권한순') {
          // 권한 기준 정렬 (오름차순)
          const permissionA = a.querySelector('td:nth-child(4)').textContent;
          const permissionB = b.querySelector('td:nth-child(4)').textContent;
          return permissionA.localeCompare(permissionB);
        }
        return 0;
      });
      
      // 정렬된 행을 테이블에 다시 추가
      const tbody = table.querySelector('tbody');
      rows.forEach(row => tbody.appendChild(row));
    });
  },
  
  /**
   * 테이블 초기화 (모든 기능 한번에 설정)
   * @param {Object} options - 설정 옵션
   */
  initTable: function(options = {}) {
    // 기본 옵션 설정
    const defaultOptions = {
      tableSelector: '.theme-table',
      filterSelector: '.filter-dropdown select',
      columnWidthOptions: {},
      onRowClick: null,
      paginationSelector: '.pagination' // 페이지네이션 선택자 추가
    };
    
    // 옵션 병합
    const settings = {...defaultOptions, ...options};
    
    // 열 너비 초기화
    this.initColumnWidths(settings.tableSelector, settings.columnWidthOptions);
    
    // 체크박스 초기화
    this.initCheckboxes(settings.tableSelector);
    
    // 필터 초기화
    this.initFilter(settings.filterSelector, settings.tableSelector);
    
    // 페이지네이션 초기화
    this.initPagination(settings.paginationSelector, settings.tableSelector);
    
    // 행 클릭 이벤트 초기화
    if (settings.onRowClick) {
      this.initRowClick(settings.tableSelector, settings.onRowClick);
    }
    
    // 창 크기 변경 시 열 너비 재계산
    window.addEventListener('resize', () => {
      this.initColumnWidths(settings.tableSelector, settings.columnWidthOptions);
    });
  },

  /**
   * 페이지네이션 기능 초기화
   * @param {string} paginationSelector - 페이지네이션 선택자
   * @param {string} tableSelector - 테이블 선택자
   * @param {Object} options - 페이지네이션 옵션
   */
  initPagination: function(paginationSelector = '.pagination', tableSelector = '.theme-table', options = {}) {
    const pagination = document.querySelector(paginationSelector);
    const table = document.querySelector(tableSelector);
    
    if (!pagination || !table) return;
    
    // 기본 옵션 설정
    const defaultOptions = {
      rowsPerPage: 10,         // 페이지당 행 수
      currentPage: 1,          // 현재 페이지
      pageNumberCount: 5       // 표시할 페이지 번호 수
    };
    
    // 옵션 병합
    const settings = {...defaultOptions, ...options};
    
    // 페이지 버튼 이벤트 설정
    const pageButtons = pagination.querySelectorAll('.page-number');
    const prevButton = pagination.querySelector('.page-nav:first-child');
    const nextButton = pagination.querySelector('.page-nav:last-child');
    
    // 페이지 번호 버튼 클릭 이벤트
    pageButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        // 현재 활성화된 버튼에서 active 클래스 제거
        pageButtons.forEach(btn => btn.classList.remove('active'));
        
        // 클릭한 버튼에 active 클래스 추가
        this.classList.add('active');
        
        // 페이지 변경 로직 (실제 구현 시 여기에 테이블 행 표시/숨김 로직 추가)
        settings.currentPage = parseInt(this.textContent);
        console.log('페이지 변경:', settings.currentPage);
        
        // 추후에 확장을 위한 페이지 변경 함수 호출
        handlePageChange(settings.currentPage);
      });
    });
    
    // 이전/다음 버튼 클릭 이벤트
    if (prevButton) {
      prevButton.addEventListener('click', function(e) {
        e.preventDefault();
        
        // 현재 활성화된 페이지 번호 확인
        const activePage = pagination.querySelector('.page-number.active');
        if (!activePage) return;
        
        const currentPageNum = parseInt(activePage.textContent);
        if (currentPageNum <= 1) return;
        
        // 이전 페이지 버튼 찾기
        const prevPageBtn = pagination.querySelector(`.page-number:nth-child(${currentPageNum})`);
        if (prevPageBtn) {
          prevPageBtn.click();
        }
      });
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', function(e) {
        e.preventDefault();
        
        // 현재 활성화된 페이지 번호 확인
        const activePage = pagination.querySelector('.page-number.active');
        if (!activePage) return;
        
        const currentPageNum = parseInt(activePage.textContent);
        const maxPageNum = pageButtons.length;
        
        if (currentPageNum >= maxPageNum) return;
        
        // 다음 페이지 버튼 찾기
        const nextPageBtn = pagination.querySelector(`.page-number:nth-child(${currentPageNum + 2})`);
        if (nextPageBtn) {
          nextPageBtn.click();
        }
      });
    }
    
    // 페이지 변경 시 처리 함수
    function handlePageChange(pageNum) {
      // 여기에 테이블 행 페이지네이션 로직 구현
      // (실제 구현 시 행 표시/숨김 처리)
    }
  }
};

// 페이지 로드 시 자동 초기화
document.addEventListener('DOMContentLoaded', function() {
  // 기본 테이블 초기화
  // 각 페이지에서 필요에 따라 옵션을 변경하여 호출할 수 있음
  if (document.querySelector('.theme-table')) {
    tableManager.initTable();
  }
}); 